import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Building2, Users, Ticket, DollarSign, Server } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import PanelGate from "@/components/PanelGate";

export default function AdminPanel() {
  return (
    <PanelGate>
      <AdminPanelInner />
    </PanelGate>
  );
}

function AdminPanelInner() {
  const t = useT();
  const { lang } = useLang();
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [bridgeRows, setBridgeRows] = useState([]);
  const [bridgeAsOf, setBridgeAsOf] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Forbidden"));
    api.get("/admin/orgs").then((r) => setOrgs(r.data.orgs)).catch(() => {});
    const loadBridge = () => {
      api.get("/admin/bridge-status").then((r) => { setBridgeRows(r.data.rows); setBridgeAsOf(r.data.as_of); }).catch(() => {});
    };
    loadBridge();
    const itv = setInterval(loadBridge, 15000);
    return () => clearInterval(itv);
  }, []);

  if (err) {
    return (
      <div className="p-12">
        <h1 className="font-display font-black text-3xl">Forbidden</h1>
        <p className="text-white/50 mt-2 text-sm">{err}</p>
      </div>
    );
  }
  if (!overview) return null;

  const mrr = lang === "pt"
    ? `R$ ${(overview.mrr_brl || 0).toLocaleString("pt-BR")}`
    : `$${(overview.mrr_usd || 0).toLocaleString("en-US")}`;

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="overline mb-3 text-primary">{t("admin.overline")}</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-10">{t("admin.title")}</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <KPI label={t("admin.kpi.orgs")} value={overview.total_orgs} Icon={Building2} testid="admin-kpi-orgs" />
        <KPI label={t("admin.kpi.users")} value={overview.total_users} Icon={Users} testid="admin-kpi-users" />
        <KPI label={t("admin.kpi.cards")} value={overview.total_cards} Icon={Ticket} testid="admin-kpi-cards" />
        <KPI label="MRR" value={mrr} Icon={DollarSign} testid="admin-kpi-mrr" />
      </div>

      <div className="surface p-6 mb-10">
        <div className="overline mb-4">{t("admin.dist")}</div>
        <div className="space-y-3">
          {Object.entries(overview.by_plan).map(([plan, count]) => {
            const pct = overview.total_orgs ? (count / overview.total_orgs) * 100 : 0;
            return (
              <div key={plan}>
                <div className="flex justify-between font-mono text-xs mb-1">
                  <span className="uppercase">{plan}</span>
                  <span>{count} · {pct.toFixed(0)}%</span>
                </div>
                <div className="h-1 bg-white/10"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bridge Status — Customer Success widget */}
      <div className="surface mb-10" data-testid="admin-bridge-status">
        <div className="p-5 pb-2 flex justify-between items-end">
          <div>
            <div className="overline flex items-center gap-2"><Server className="w-3.5 h-3.5" /> {lang === "pt" ? "Status do Bridge · Customer Success" : "Bridge Status · Customer Success"}</div>
            <div className="text-xs text-white/40 mt-1 font-mono">
              {lang === "pt" ? "Atualizado a cada 15s · verde = ativo < 60s" : "Refreshes every 15s · green = active < 60s"}
              {bridgeAsOf && <> · {new Date(bridgeAsOf).toLocaleTimeString()}</>}
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-white/10">
            <tr className="text-left">
              <th className="p-4 overline">Org</th>
              <th className="p-4 overline">Plan</th>
              <th className="p-4 overline">Bridge</th>
              <th className="p-4 overline">{lang === "pt" ? "Última atividade" : "Last seen"}</th>
              <th className="p-4 overline">{lang === "pt" ? "Cards 24h" : "Cards 24h"}</th>
              <th className="p-4 overline">MRR</th>
            </tr>
          </thead>
          <tbody>
            {bridgeRows.map((r) => {
              const mrrStr = r.subscription_status === "active"
                ? (lang === "pt" ? `R$${(r.mrr_brl || 0).toLocaleString("pt-BR")}` : `$${(r.mrr_usd || 0).toLocaleString("en-US")}`)
                : "—";
              return (
                <tr key={r.org_id} className="border-b border-white/5" data-testid={`admin-bridge-row-${r.org_id}`}>
                  <td className="p-4">{r.org_name}</td>
                  <td className="p-4 font-mono text-xs">{r.plan}</td>
                  <td className="p-4">
                    {r.bridge_count === 0 ? (
                      <span className="font-mono text-xs text-white/40">{lang === "pt" ? "nenhuma key" : "no keys"}</span>
                    ) : (
                      <span className="font-mono text-xs inline-flex items-center gap-1.5">
                        <span className={`status-dot ${r.bridge_online ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
                        {r.bridge_online ? "online" : "offline"} · {r.bridge_count} key{r.bridge_count > 1 ? "s" : ""}
                      </span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs text-white/50">
                    {r.bridge_last_seen_at ? new Date(r.bridge_last_seen_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-4 font-mono text-xs">{r.cards_24h}</td>
                  <td className="p-4 font-mono text-xs text-primary">{mrrStr}</td>
                </tr>
              );
            })}
            {bridgeRows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-white/40 text-sm">{lang === "pt" ? "Sem dados ainda" : "No data yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="surface">
        <div className="overline p-5 pb-2">{t("admin.orgs")}</div>
        <table className="w-full text-sm">
          <thead className="border-b border-white/10">
            <tr className="text-left">
              <th className="p-4 overline">Name</th>
              <th className="p-4 overline">Plan</th>
              <th className="p-4 overline">Status</th>
              <th className="p-4 overline">Members</th>
              <th className="p-4 overline">Cards</th>
              <th className="p-4 overline">Created</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-white/5" data-testid={`admin-org-${o.id}`}>
                <td className="p-4">{o.name}</td>
                <td className="p-4 font-mono text-xs">{o.plan}</td>
                <td className="p-4"><span className={`status-dot ${o.subscription_status === "active" ? "bg-emerald-400" : "bg-amber-400"}`} /> <span className="font-mono text-xs ml-1">{o.subscription_status}</span></td>
                <td className="p-4 font-mono text-xs">{o._members}</td>
                <td className="p-4 font-mono text-xs">{o._cards}</td>
                <td className="p-4 font-mono text-xs text-white/40">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value, Icon, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="flex justify-between items-start mb-3">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-white/30" />
      </div>
      <div className="font-mono text-3xl font-bold">{value}</div>
    </div>
  );
}
